import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Link as MuiLink,
  TextField,
  MenuItem,
  Stack,
  Tooltip,
  Button,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

// Empty string for same-origin API calls (nginx proxies /api/)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

const FORM_OPTIONS = [
  { value: 'all', label: 'All forms' },
  { value: '8-K', label: '8-K only' },
  { value: '8-K/A', label: '8-K/A (amended) only' },
  { value: '10-Q', label: '10-Q only' },
  { value: '10-Q/A', label: '10-Q/A (amended) only' },
  { value: '10-K', label: '10-K only' },
  { value: '10-K/A', label: '10-K/A (amended) only' },
  { value: 'S-1', label: 'S-1 only' },
  { value: 'S-1/A', label: 'S-1/A (amended) only' },
  { value: 'F-1', label: 'F-1 only' },
  { value: 'F-1/A', label: 'F-1/A (amended) only' },
  { value: 'S-4', label: 'S-4 only' },
  { value: 'S-4/A', label: 'S-4/A (amended) only' },
  { value: '6-K', label: '6-K only' },
  { value: '6-K/A', label: '6-K/A (amended) only' },
  { value: '20-F', label: '20-F only' },
  { value: '20-F/A', label: '20-F/A (amended) only' },
];

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch (e) {
    return String(value);
  }
};

const SecFilings = ({ ticker }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formFilter, setFormFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!ticker) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (formFilter !== 'all') params.set('form', formFilter);
    const qs = params.toString();
    const url = `${API_BASE_URL}/api/sec-filings/${encodeURIComponent(ticker)}/all${qs ? `?${qs}` : ''}`;

    fetch(url)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(body?.error || `HTTP ${r.status}`);
        }
        return body;
      })
      .then((body) => {
        if (cancelled) return;
        setData(body);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load filings');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ticker, formFilter, reloadKey]);

  const years = useMemo(() => {
    if (!data?.countsByYear) return [];
    return Object.keys(data.countsByYear)
      .map((y) => Number(y))
      .filter((y) => Number.isFinite(y))
      .sort((a, b) => b - a);
  }, [data]);

  const filteredFilings = useMemo(() => {
    const filings = data?.filings || [];
    if (yearFilter === 'all') return filings;
    const yearNum = Number(yearFilter);
    return filings.filter((f) => {
      const y = f.fiscalYear || (f.filingDate ? new Date(f.filingDate).getUTCFullYear() : null);
      return y === yearNum;
    });
  }, [data, yearFilter]);

  if (!ticker) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Select a ticker to view its SEC filings.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
             justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DescriptionIcon color="primary" />
            <Typography variant="h5" component="h2">
              SEC Filings — {ticker}
            </Typography>
            {data?.cik && (
              <Chip
                size="small"
                label={`CIK ${Number(data.cik)}`}
                variant="outlined"
                component="a"
                clickable
                target="_blank"
                rel="noopener noreferrer"
                href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${Number(data.cik)}&owner=include&count=40`}
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            SEC EDGAR filings including current reports (8-K), financial filings, and registration statements.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            select size="small" label="Form" value={formFilter}
            onChange={(e) => setFormFilter(e.target.value)} sx={{ minWidth: 160 }}
          >
            {FORM_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Year" value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)} sx={{ minWidth: 120 }}
            disabled={!years.length}
          >
            <MenuItem value="all">All years</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y} ({data.countsByYear[y]})
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title="Refresh">
            <span>
              <Button
                size="small" variant="outlined" startIcon={<RefreshIcon />}
                onClick={() => setReloadKey((k) => k + 1)} disabled={loading}
              >
                Reload
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 4 }}>
          <CircularProgress size={20} /> <Typography>Loading filings…</Typography>
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
          {error.toLowerCase().includes('sec_filings table') && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Populate it from the host with:&nbsp;
                <code>python app/ETL/GetAll10QFilings.py {ticker}</code>
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {!loading && !error && data && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`${data.count} filing${data.count === 1 ? '' : 's'} stored`} />
            <Chip label={`${filteredFilings.length} shown`} variant="outlined" />
            {years.length > 0 && (
              <Chip label={`${years[years.length - 1]} – ${years[0]}`} variant="outlined" />
            )}
          </Stack>

          {filteredFilings.length === 0 ? (
            <Alert severity="info">No filings match the current filters.</Alert>
          ) : (
            <Paper variant="outlined">
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Filing date</TableCell>
                      <TableCell>Report period</TableCell>
                      <TableCell>Form</TableCell>
                      <TableCell>FY</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Accession #</TableCell>
                      <TableCell align="right">Document</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFilings.map((f) => (
                      <TableRow key={f.accessionNumber} hover>
                        <TableCell>{formatDate(f.filingDate)}</TableCell>
                        <TableCell>{formatDate(f.reportDate)}</TableCell>
                        <TableCell>
                          <Chip size="small"
                              color={f.form.endsWith('/A') ? 'warning' : 'primary'}
                                label={f.form} variant="outlined" />
                        </TableCell>
                        <TableCell>{f.fiscalYear || '—'}</TableCell>
                        <TableCell>{f.fiscalPeriod || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {f.accessionNumber}
                        </TableCell>
                        <TableCell align="right">
                          {f.filingUrl ? (
                            <MuiLink href={f.filingUrl} target="_blank" rel="noopener noreferrer"
                                     underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                              Open <OpenInNewIcon fontSize="inherit" />
                            </MuiLink>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default SecFilings;
